import { Fragment, useId, useRef } from 'react';
import { apiErrorText, useGetFsnbCoefficientQuery } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { Drawer, DrawerBody, DrawerCloseButton, DrawerHeader } from '@shared/ui/Drawer';
import {
  BASE_TYPE_TITLES,
  COEF_PARTS,
  coefDecreeText,
  coefResourceText,
  coefScopeLabel,
  coefValueText,
  fullCode,
} from '../../normBaseLabels';
import {
  Err,
  Eyebrow,
  Grid,
  HeaderRow,
  Meta,
  Muted,
  MutedNote,
  Name,
  Section,
  SectionTitle,
  TableWrap,
  Title,
  Titles,
  ValuesGrid,
} from '../styles';

/** Карточка поправочного коэффициента: значения, свои значения для ресурсов и
 *  нормы, таблицы, сборники, к которым он привязан. */
export function CoefficientCardDrawer({
  code,
  edition,
  onClose,
  onOpenWork,
}: {
  code: string;
  edition: string;
  onClose: () => void;
  onOpenWork?: (baseType: string, code: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const q = useGetFsnbCoefficientQuery({ code, edition });
  const card = q.data?.card;
  const c = card?.coefficient;
  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} size="lg" initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>
              Поправочный коэффициент
              {c?.base_type && BASE_TYPE_TITLES[c.base_type] ? ` · ${c.base_type}` : ''}
            </Eyebrow>
            <Title id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              {code}
            </Title>
          </Titles>
          <DrawerCloseButton onClick={onClose} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        {q.isLoading && <Muted>Загрузка…</Muted>}
        {q.error && <Err>{apiErrorText(q.error, 'Коэффициент не удалось открыть.')}</Err>}
        {card && c && (
          <>
            <Name>{c.name}</Name>
            <Meta>
              <span>
                Введён: <strong>{coefDecreeText(c.decree)}</strong>
              </span>
              {c.section_name && (
                <span>
                  Техническая часть: <strong>{c.section_name}</strong>
                </span>
              )}
              {c.target && (
                <span>
                  Применяется:{' '}
                  <strong>{c.target === 'Расход' ? 'к расходу ресурсов нормы' : c.target}</strong>
                </span>
              )}
            </Meta>
            <Section>
              <SectionTitle>Значения</SectionTitle>
              <ValuesGrid>
                {COEF_PARTS.map((p) => (
                  <Fragment key={p.key}>
                    <dt>{p.title}</dt>
                    <dd>{coefValueText(c, p.key)}</dd>
                  </Fragment>
                ))}
              </ValuesGrid>
              {COEF_PARTS.some((p) => c[`${p.key}_formula` as const]) && (
                <MutedNote>
                  Значение задано формулой: N — величина из условия в наименовании (число проходов,
                  каждые 100 мм и т. п.). Подставляет её сметчик.
                </MutedNote>
              )}
            </Section>
            {card.resources.length > 0 && (
              <Section>
                <SectionTitle>Свои значения для ресурсов</SectionTitle>
                <TableWrap>
                  <Grid>
                    <thead>
                      <tr>
                        <th>Шифр</th>
                        <th>Ресурс</th>
                        <th>Значение</th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.resources.map((r, i) => (
                        <tr key={`${r.resource_code}-${i}`}>
                          <td className="code">{r.resource_code}</td>
                          <td>
                            {r.is_class
                              ? 'Класс ресурсов — все ресурсы с этим началом шифра'
                              : (r.resource_name ?? '—')}
                          </td>
                          <td>{coefResourceText(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Grid>
                </TableWrap>
              </Section>
            )}
            <Section>
              <SectionTitle>
                Применяется{card.scopes_total > 0 ? `: ${fmtNum(card.scopes_total)}` : ''}
              </SectionTitle>
              {card.scopes.length === 0 ? (
                <Muted>В привязке ГГЭ к нормам не привязан — применяется по техчасти.</Muted>
              ) : (
                <TableWrap>
                  <Grid>
                    <thead>
                      <tr>
                        <th>Привязка</th>
                        <th>Шифр</th>
                        <th>Наименование</th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.scopes.map((s) => {
                        const openable = s.scope === 'work' && Boolean(onOpenWork);
                        return (
                          <tr
                            key={`${s.scope}-${s.base_type}-${s.code}`}
                            className={openable ? 'clickable' : undefined}
                            onClick={
                              openable && onOpenWork
                                ? () => onOpenWork(s.base_type, s.code)
                                : undefined
                            }
                          >
                            <td className="nowrap">{coefScopeLabel(s.scope)}</td>
                            <td className="code">{fullCode(s.base_type, s.code)}</td>
                            <td>{s.name ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Grid>
                </TableWrap>
              )}
              {card.scopes_total > card.scopes.length && (
                <MutedNote>
                  Показаны первые {card.scopes.length} из {fmtNum(card.scopes_total)}.
                </MutedNote>
              )}
            </Section>
          </>
        )}
      </DrawerBody>
    </Drawer>
  );
}
